import { useNavigate } from 'react-router-dom';
import { useRef, useState, useEffect, useCallback } from 'react';
import { getOrCreateVisitorId } from '../hooks/usePageTracking';
import { useAuth } from '../hooks/useAuth';
import YouTubePlayer from '../components/YouTubePlayer';

function EnterPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isApproved, isAuthor } = useAuth();
  const [downloading, setDownloading] = useState(false);

  // Auto-redirect authors, editors, and reviewers to the reviewer portal
  useEffect(() => {
    if (isAuthenticated && isApproved && user?.role !== 'commenter') {
      navigate('/reviewer', { replace: true });
    }
  }, [isAuthenticated, isApproved, user?.role, navigate]);
  const [hoverWeb, setHoverWeb] = useState(false);
  const [hoverPrint, setHoverPrint] = useState(false);
  const [hoverNote, setHoverNote] = useState(false);
  const startTime = useRef(Date.now());
  const maxProgress = useRef(0);

  const handleProgress = useCallback((pct: number) => {
    if (pct > maxProgress.current) maxProgress.current = pct;
  }, []);

  const handleEnded = useCallback(() => {
    navigate('/essays/the-deal');
  }, [navigate]);

  // Notify server of arrival immediately (triggers new-visitor alert)
  useEffect(() => {
    fetch('/api/public/arrive', { method: 'POST' }).catch(() => {});
  }, []);

  // Send beacon with duration + video progress on page leave
  useEffect(() => {
    function sendBeacon() {
      const duration = Math.round((Date.now() - startTime.current) / 1000);
      if (duration < 1) return;
      const payload = JSON.stringify({
        p: '/',
        d: duration,
        vp: maxProgress.current,
        vid: getOrCreateVisitorId(),
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/public/v',
          new Blob([payload], { type: 'application/json' })
        );
      }
    }

    function onVisChange() {
      if (document.visibilityState === 'hidden') sendBeacon();
    }

    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('pagehide', sendBeacon);

    return () => {
      sendBeacon();
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('pagehide', sendBeacon);
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#0a0a0f',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        overflow: 'auto',
        padding: '16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '960px', flexShrink: 1 }}>
        <YouTubePlayer
          videoId="SORnfJtaeSo"
          onProgress={handleProgress}
          onEnded={handleEnded}
        />
      </div>
      <div
        style={{
          marginTop: '24px',
          flexShrink: 0,
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={() => navigate('/essays/the-deal')}
          onMouseEnter={() => setHoverWeb(true)}
          onMouseLeave={() => setHoverWeb(false)}
          style={{
            background: hoverWeb
              ? 'linear-gradient(135deg, #e0bf4a 0%, #d4af37 100%)'
              : 'linear-gradient(135deg, #d4af37 0%, #b8962e 100%)',
            color: '#1a1a1a',
            border: '1px solid rgba(212, 175, 55, 0.5)',
            padding: '14px 36px',
            borderRadius: '4px',
            fontFamily: "var(--font-serif)",
            fontSize: '1rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase' as const,
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            transform: hoverWeb ? 'translateY(-1px)' : 'none',
            boxShadow: hoverWeb ? '0 4px 20px rgba(212, 175, 55, 0.3)' : 'none',
          }}
        >
          Web Experience
        </button>
        <button
          onClick={async () => {
            setDownloading(true);
            try {
              const res = await fetch('/api/public/print-pdf');
              if (!res.ok) throw new Error('PDF generation failed');
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Compendium-of-All-My-Knowledge.pdf';
              a.click();
              URL.revokeObjectURL(url);
            } catch {
              alert('Failed to generate PDF. Please try again.');
            } finally {
              setDownloading(false);
            }
          }}
          onMouseEnter={() => setHoverPrint(true)}
          onMouseLeave={() => setHoverPrint(false)}
          disabled={downloading}
          style={{
            background: downloading
              ? 'linear-gradient(135deg, #a08828 0%, #8a7520 100%)'
              : hoverPrint
                ? 'linear-gradient(135deg, #e0bc3f 0%, #d4af37 100%)'
                : 'linear-gradient(135deg, #d4af37 0%, #b8962e 100%)',
            color: '#0a0a0f',
            border: '1px solid rgba(212, 175, 55, 0.5)',
            padding: '14px 36px',
            borderRadius: '4px',
            fontFamily: "var(--font-serif)",
            fontSize: '1rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase' as const,
            cursor: downloading ? 'wait' : 'pointer',
            transition: 'all 0.25s ease',
            transform: hoverPrint && !downloading ? 'translateY(-1px)' : 'none',
            boxShadow: hoverPrint && !downloading ? '0 4px 20px rgba(212, 175, 55, 0.3)' : 'none',
            opacity: downloading ? 0.7 : 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <span>{downloading ? 'Generating...' : 'Print Version'}</span>
          {!downloading && (
            <span style={{ fontSize: '0.65rem', letterSpacing: '0.1em', opacity: 0.8 }}>
              Recommended
            </span>
          )}
        </button>
        {isAuthor && (
          <button
            onClick={() => navigate('/reviewer')}
            onMouseEnter={() => setHoverNote(true)}
            onMouseLeave={() => setHoverNote(false)}
            style={{
              background: hoverNote
                ? 'linear-gradient(135deg, #e0bf4a 0%, #d4af37 100%)'
                : 'linear-gradient(135deg, #d4af37 0%, #b8962e 100%)',
              color: '#1a1a1a',
              border: '1px solid rgba(212, 175, 55, 0.5)',
              padding: '14px 36px',
              borderRadius: '4px',
              fontFamily: "var(--font-serif)",
              fontSize: '1rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase' as const,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              transform: hoverNote ? 'translateY(-1px)' : 'none',
              boxShadow: hoverNote ? '0 4px 20px rgba(212, 175, 55, 0.3)' : 'none',
            }}
          >
            Author
          </button>
        )}
      </div>
    </div>
  );
}

export default EnterPage;
